import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';

import type { SelectedFilters } from '@/hooks/filters/useCompanyAttributesFilters';
import type { Company } from '@/types/directory/Company';
import type { CompanyAttributeMap } from '@/utils/company';

import { getCompanyAttributeList } from '@/utils/company';
import { familiesSeededBy, selectedIdsKey } from '@/utils/companyAttributeFilters';

type UseFollowedSelectionParams = {
    selection?: SelectedFilters;
    company?: Company;
    filters: SelectedFilters;
    setFilters: Dispatch<SetStateAction<SelectedFilters>>;
    setSelectedCompanyAttributes: Dispatch<SetStateAction<CompanyAttributeMap>>;
    onFiltersChange: (filters: SelectedFilters) => void;
};

/**
 * Keeps the picker's own `filters` and the caller's `selection` in step, both ways.
 *
 * Down: a held selection is followed when it changes from elsewhere (a navigation that clears it,
 * a value no longer offered), and once the company's attributes arrive, since until then no family
 * can open to show it.
 *
 * Up: every change of `filters` is announced, except the selection the caller already holds. The
 * reader's own pick therefore comes back as that same selection and stops there, leaving the
 * picker -- and its open menu -- as it is. Left without a `selection`, every change is announced.
 */
export const useFollowedSelection = ({
    selection,
    company,
    filters,
    setFilters,
    setSelectedCompanyAttributes,
    onFiltersChange,
}: UseFollowedSelectionParams) => {
    const selectionKey = selection && selectedIdsKey(selection);
    const hasCompany = !!company;
    const [followed, setFollowed] = useState({ selectionKey, hasCompany });

    const hasCompanyChanged = followed.hasCompany !== hasCompany;
    if (selection && (hasCompanyChanged || followed.selectionKey !== selectionKey)) {
        setFollowed({ selectionKey, hasCompany });

        const hasSelectionMovedElsewhere = selectionKey !== selectedIdsKey(filters);
        if (hasCompanyChanged || hasSelectionMovedElsewhere) {
            setFilters(selection);
            setSelectedCompanyAttributes(
                familiesSeededBy(getCompanyAttributeList(company), selection),
            );
        }
    }

    // Both are read through a ref rather than depended on: a caller that writes the callback inline
    // hands a new function at every render, and re-announcing the unchanged selection to it undoes
    // whatever that caller derives from a real filter change -- it is what sent the meetings list
    // back to its first page on every render.
    const onFiltersChangeRef = useRef(onFiltersChange);
    onFiltersChangeRef.current = onFiltersChange;
    const selectionKeyRef = useRef(selectionKey);
    selectionKeyRef.current = selectionKey;

    useEffect(() => {
        if (selectedIdsKey(filters) === selectionKeyRef.current) {
            return;
        }

        onFiltersChangeRef.current(filters);
    }, [filters]);
};
